import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ProviderPoolEditDialog } from "@/features/provider-pool/provider-pool-edit-dialog";
import {
  ChevronLeft,
  Edit,
  Loader2,
  Lock,
  Plus,
  Search,
  Trash2,
  User,
  Users,
  AlertCircle,
  RefreshCw,
  MoreVertical,
} from "lucide-react";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { toast } from "sonner";
import { ProviderSharedSelect } from "@/components/Selects/ProviderSharedSelect";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChatDialog } from "@/features/chat/chat-dialog";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import { useOrgPath } from "@/hooks/useOrgPath";
import { RecordNotInBusinessUnit } from "@/components/page-message";
import {
  isRecordUnavailableError,
  retryUnlessRecordUnavailable,
} from "@/common/utils/record-unavailable-error";
import { RevealProviderButton } from "@/features/provider/reveal-provider-button";
import { OrganizationProviderChatButton } from "@/features/chat/organization-provider-chat-button";
import { PoolMonitoringBar } from "@/features/psv-monitoring/pool-monitoring-bar";
import { MONITORING_COPY } from "@/features/psv-monitoring/monitoring-constants";
import { PsvMonitoringProgramStatus } from "@api/shared-runtime/psv-monitoring";

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/providers/pools/$id/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { user } = Route.useRouteContext();
  const { hasPermission } = useRolePermissions(user);
  const canView = hasPermission(Permission.ViewProviderPools);
  const canManage = hasPermission(Permission.ManageProviderPools);
  const canEditCredentials = hasPermission(Permission.EditProviderCredentials);
  const canRevealProviders = hasPermission(Permission.ViewJobOffers);
  const { selectedOrgBusinessUnit } = useOrganization();
  const orgPath = useOrgPath();
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{
    userId: string;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [selectedProviders, setSelectedProviders] = useState<
    Array<{ name: string; id: string }>
  >([]);
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  // Fetch pool details
  const {
    data: pool,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    trpc.providerPool.get.queryOptions(
      {
        id,
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      },
      { retry: retryUnlessRecordUnavailable },
    ),
  );

  // Shares its cache entry with PoolMonitoringBar. Users restricted to pools
  // get FORBIDDEN, which just means no monitoring consequence copy.
  const monitoringEnabled =
    !!selectedOrgBusinessUnit.enablePrimarySourceVerification;
  const { data: poolMonitoring } = useQuery({
    ...trpc.psv.monitoring.getPoolMonitoring.queryOptions(
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        providerPoolId: id,
      },
      { retry: false },
    ),
    enabled: monitoringEnabled && canView,
  });
  const isPoolMonitored =
    poolMonitoring?.rules.some(
      (rule) => rule.status === PsvMonitoringProgramStatus.Active,
    ) ?? false;

  // Delete pool mutation
  const removeMemberMutation = useMutation(
    trpc.providerPool.removeMember.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.providerPool.get.queryKey({
            id,
            organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          }),
        });
        toast.success("Member removed successfully");
        setRemoveMemberDialogOpen(false);
        setMemberToRemove(null);
      },
      onError: (error) => {
        toast.error(`Failed to remove member: ${error.message}`);
      },
    }),
  );

  // Add members mutation
  const addMembersMutation = useMutation(
    trpc.providerPool.addMembers.mutationOptions({
      onSuccess: async () => {
        toast.success("Members added successfully");
        setAddMemberDialogOpen(false);
        setSelectedProviders([]);
        await queryClient.invalidateQueries({
          queryKey: trpc.providerPool.get.queryKey({
            id,
            organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          }),
        });
      },
      onError: (error) => {
        toast.error(`Failed to add members: ${error.message}`);
      },
    }),
  );

  const deleteMutation = useMutation(
    trpc.providerPool.delete.mutationOptions({
      onSuccess: () => {
        toast.success("Provider pool deleted successfully");
        navigate({ to: orgPath("/providers/pools") });
      },
    }),
  );
  const [chattingProvider, setChattingProvider] = useState<{
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [viewingProvider, setViewingProvider] = useState<{
    id: string;
  } | null>(null);

  // Filter members based on search term
  const filteredMembers = pool?.members?.filter((member) => {
    const memberWithReveal = member as typeof member & {
      isRevealed?: boolean;
    };
    const displayName =
      memberWithReveal.isRevealed === false
        ? `Provider #${member.userId.slice(-4)}`
        : `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();
    return (
      searchTerm === "" ||
      displayName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (!canView) {
    return (
      <div className="container mx-auto py-8 px-2">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <Link
              to={orgPath("/providers/pools")}
              className="flex items-center text-primary hover:text-primary/80 mb-4"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Provider Pools
            </Link>
          </div>
          <Card className="border-dashed">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center gap-4">
              <div className="bg-slate-100 p-3 rounded-full">
                <Lock className="h-8 w-8 text-slate-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-1">
                  Access Restricted
                </h3>
                <p className="text-gray-600 max-w-md">
                  You don't have permission to view provider pools. Contact your
                  administrator to request access.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isRecordUnavailableError(error)) {
    return (
      <RecordNotInBusinessUnit
        title="Provider pool not found"
        recordLabel="provider pool"
        back={{
          to: orgPath("/providers/pools"),
          label: "Back to provider pools",
        }}
      />
    );
  }

  const handleDeletePool = () => {
    deleteMutation.mutate({
      id,
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    });
  };

  const handleAddMembers = () => {
    if (selectedProviders.length === 0) {
      toast.error("Please select at least one provider");
      return;
    }

    addMembersMutation.mutate({
      userIds: selectedProviders.map((provider) => provider.id),
      providerPoolId: id,
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    });
  };

  const handleRemoveMember = () => {
    if (!memberToRemove) {
      return;
    }

    removeMemberMutation.mutate({
      providerPoolId: id,
      userId: memberToRemove.userId,
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    });
  };

  const handleEditSuccess = async () => {
    setEditDialogOpen(false);
    await queryClient.invalidateQueries({
      queryKey: trpc.providerPool.get.queryKey({
        id,
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      }),
    });
  };

  return (
    <div className="container mx-auto py-8 px-2">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <Link
            to={orgPath("/providers/pools")}
            className="flex items-center text-primary hover:text-primary/80 mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Provider Pools
          </Link>

          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading pool details...</span>
            </div>
          ) : isError ? (
            <div className="text-red-500 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>
                {error instanceof Error
                  ? error.message
                  : "Failed to load pool details"}
              </span>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-semibold">{pool?.name}</h1>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1 h-auto"
                      onClick={() => setEditDialogOpen(true)}
                    >
                      <Edit
                        size={16}
                        className="text-gray-500 hover:text-gray-700"
                      />
                    </Button>
                  )}
                </div>
                {pool?.description && (
                  <p className="text-gray-600 mt-1">{pool.description}</p>
                )}
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <Button
                    className="flex items-center gap-2"
                    variant="outline"
                    onClick={() => setAddMemberDialogOpen(true)}
                  >
                    <Plus size={16} />
                    Add Members
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="text-red-600 hover:text-red-800 focus:text-red-800 cursor-pointer"
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        <Trash2 size={16} className="mr-2" />
                        Delete Pool
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          )}
        </div>

        {monitoringEnabled && pool && (
          <PoolMonitoringBar
            organizationBusinessUnitId={selectedOrgBusinessUnit.id}
            providerPoolId={id}
            poolName={pool.name}
            canEdit={canEditCredentials}
          />
        )}

        <div className="mb-6 grid min-w-0 gap-2">
          <Label htmlFor="provider-pool-members-search">Search members</Label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <Input
              id="provider-pool-members-search"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {isLoading ? (
          <Card className="border-dashed">
            <CardContent className="p-8 flex flex-col items-center justify-center text-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
              <h3 className="text-xl font-semibold">Loading members</h3>
            </CardContent>
          </Card>
        ) : isError ? (
          <Card className="border-dashed border-2 border-red-300 bg-red-50">
            <CardContent className="p-8 flex flex-col items-center justify-center text-center">
              <AlertCircle className="h-8 w-8 text-red-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">
                Failed to load members
              </h3>
              <p className="text-gray-600 mb-6 max-w-md">
                {error instanceof Error
                  ? error.message
                  : "An error occurred while loading pool members."}
              </p>
              <Button onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : filteredMembers && filteredMembers.length > 0 ? (
          <Card className="p-0 overflow-hidden shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Specialties</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member) => {
                  const memberWithReveal = member as typeof member & {
                    isRevealed?: boolean;
                  };
                  const isHidden = memberWithReveal.isRevealed === false;
                  const displayName = isHidden
                    ? `Provider #${member.userId.slice(-4)}`
                    : `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();

                  return (
                    <TableRow key={member.id} className="hover:bg-gray-50">
                      <TableCell className="py-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/10">
                            {isHidden ? (
                              <User className="h-5 w-5 text-gray-400" />
                            ) : (
                              member.firstName?.[0] ||
                              member.lastName?.[0] || (
                                <User className="h-5 w-5 text-primary" />
                              )
                            )}
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <div className="min-w-0">
                          <Button
                            variant="link"
                            className="text-primary hover:underline font-medium p-0 h-auto"
                            onClick={() =>
                              setViewingProvider({ id: member.userId })
                            }
                          >
                            {displayName}
                          </Button>
                          {member.isHired && (
                            <div className="mt-1">
                              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">
                                Hired
                              </Badge>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isHidden
                          ? "Hidden until revealed"
                          : member.specialties
                              .map((specialty) => specialty.name)
                              .join(", ")}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isHidden && canRevealProviders && (
                            <RevealProviderButton
                              providerUserId={member.userId}
                              organizationBusinessUnitId={
                                selectedOrgBusinessUnit.id
                              }
                              size="sm"
                            />
                          )}
                          {!isHidden && (
                            <OrganizationProviderChatButton
                              canChat={member.canChat}
                              onClick={() => {
                                setChattingProvider({
                                  id: member.userId,
                                  firstName: member.firstName,
                                  lastName: member.lastName,
                                });
                              }}
                            />
                          )}
                          {canManage && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              onClick={() => {
                                setMemberToRemove({
                                  userId: member.userId,
                                  firstName: member.firstName,
                                  lastName: member.lastName,
                                });
                                setRemoveMemberDialogOpen(true);
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-dashed">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-primary/10 p-3 rounded-full mb-4">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No members found</h3>
              <p className="text-gray-600 mb-6 max-w-md">
                {searchTerm
                  ? "There are no members matching your search criteria."
                  : "This provider pool doesn't have any members yet."}
              </p>
              {canManage && (
                <Button
                  onClick={() => setAddMemberDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Members
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Provider Pool Dialog */}
      <ProviderPoolEditDialog
        poolId={id}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the provider pool "{pool?.name}".
              This action cannot be undone.
              {isPoolMonitored && ` ${MONITORING_COPY.poolMembersStop}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeletePool}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Member Confirmation Dialog */}
      <AlertDialog
        open={removeMemberDialogOpen}
        onOpenChange={setRemoveMemberDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {memberToRemove?.firstName}{" "}
              {memberToRemove?.lastName} from the "{pool?.name}" pool?
              {isPoolMonitored && ` ${MONITORING_COPY.memberStops}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setMemberToRemove(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleRemoveMember}
              disabled={removeMemberMutation.isPending}
            >
              {removeMemberMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Members Dialog */}
      <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] w-2xl">
          <DialogHeader>
            <DialogTitle>Add Members to Pool</DialogTitle>
            <DialogDescription>
              Select providers to add to the "{pool?.name}" pool.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <ProviderSharedSelect
              value={selectedProviders}
              onChange={(value) =>
                setSelectedProviders(
                  value as Array<{ name: string; id: string }>,
                )
              }
              organizationBusinessUnitId={selectedOrgBusinessUnit?.id || ""}
              placeholder="Search for providers..."
              isMulti={true}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddMemberDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMembers}
              disabled={
                addMembersMutation.isPending || selectedProviders.length === 0
              }
            >
              {addMembersMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Add to Pool
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {viewingProvider && (
        <ProfileModal
          userId={viewingProvider.id}
          onClose={() => setViewingProvider(null)}
          organizationBusinessUnitId={selectedOrgBusinessUnit.id}
        />
      )}
      {chattingProvider && (
        <ChatDialog
          isOpen={chattingProvider !== null}
          onOpenChange={() => setChattingProvider(null)}
          user={chattingProvider}
        />
      )}
    </div>
  );
}
